import React from 'react'
import i18next from 'i18next'
import {Timeline} from 'antd'
import {getAmountWithCurrency, isEmptyOrSpaces} from '@/utils'
import {Currencies, ChargebackStatus} from '@/constants'
import {ChargebackInfoType, ChargebackType} from '@/api'
import {CardSchemeLabelWithIcon, IconTitle, TableStatus} from '@/components'
import {TagType} from '@/components/dumb/Tag/props'

import styles from '../styles.scss'

export const buildTransactionFields = (
    transaction: ChargebackInfoType,
    chargeback: ChargebackType
) => {
    if (!transaction) return []

    return [
        {label: i18next.t('ChargebacksDetails.bankingDate'), value: transaction.date, key: 'date'},
        {
            label: i18next.t('ChargebacksDetails.amount'),
            value: transaction.amount,
            key: 'amount',
            render: () =>
                getAmountWithCurrency(transaction.amount, transaction.currency as Currencies)
        },
        {
            label: i18next.t('ChargebacksDetails.captureMethod'),
            value: chargeback.captureMethod ? String(chargeback.captureMethod).toUpperCase() : '',
            key: 'captureMethod'
        },
        {
            label: i18next.t('ChargebacksDetails.transactionType'),
            value: transaction.transactionType,
            key: 'transactionType'
        },
        {
            label: i18next.t('ChargebacksDetails.cardScheme'),
            value: chargeback.cardScheme,
            key: 'cardScheme',
            render: () => (
                <CardSchemeLabelWithIcon cardScheme={chargeback.cardScheme} showCardSchemeTitle />
            )
        },
        {
            label: i18next.t('ChargebacksDetails.cardMask'),
            value: chargeback.cardMask,
            key: 'cardMask'
        },
        {label: i18next.t('ChargebacksDetails.documentId'), value: transaction.documentId, key: 'documentId'},
        {label: i18next.t('ChargebacksDetails.terminalId'), value: chargeback.terminalId, key: 'terminalId'},
        {label: i18next.t('ChargebacksDetails.transactionId'), value: chargeback.transactionId, key: 'transactionId'}
    ]
}

export const buildChargebackFields = (chargeback: ChargebackType) => {
    if (!chargeback) return []

    return [
        {
            label: i18next.t('ChargebacksDetails.chargebackDate'),
            value: chargeback.chargebackDate,
            key: 'chargebackDate'
        },
        {
            label: i18next.t('ChargebacksDetails.amount'),
            value: chargeback.amount,
            key: 'amount',
            render: () =>
                getAmountWithCurrency(chargeback.amount, chargeback.currency as Currencies)
        },
        {
            label: i18next.t('ChargebacksDetails.reasonCode'),
            value: chargeback.reasonCode,
            key: 'reasonCode',
            render: () => (
                <IconTitle
                    className={styles.iconTitle}
                    title={chargeback.reasonCode}
                    tooltipText={chargeback.reasonCodeDescription}
                />
            )
        },
        {label: i18next.t('ChargebacksDetails.arn'), value: chargeback.arn, key: 'arn'}
    ]
}

export const buildChargebackHistoryFields = (chargebackHistory: ChargebackInfoType[]) =>
    chargebackHistory?.map(({documentId, date, chargebackStatus, amount, currency, status}) => ({
        key: documentId,
        render: () => (
            <Timeline.Item key={documentId} label={`${date}`} className={styles.timelineItem}>
                {[
                    {
                        title: i18next.t('ChargebacksDetails.chargebackStatus'),

                        value: !isEmptyOrSpaces(chargebackStatus) ? (
                            <div className={styles.statusTagHolder}>
                                <TableStatus
                                    statusProps={{
                                        status: 
                                            i18next.t(`enums:chargebackStatus.${chargebackStatus}`, { defaultValue: chargebackStatus }),
                                        type:
                                            chargebackStatus ===
                                            ChargebackStatus['chargeback-reversal']
                                                ? TagType.Success
                                                : TagType.Default,
                                        iconType: 'refund'
                                    }}
                                />
                            </div>
                        ) : null
                    },
                    {
                        title: i18next.t('ChargebacksDetails.amount'),
                        value: getAmountWithCurrency(amount, currency as Currencies)
                    },
                    {
                        title: i18next.t('ChargebacksDetails.financialStatus'),
                        value: status
                    },
                    {title: i18next.t('ChargebacksDetails.documentId'), value: documentId}
                ]?.map(({title, value}, index) => (
                    <div key={index} className={styles.timelineItemRow}>
                        <div className={styles.timelineItemTitle}>{title}</div>
                        <div className={styles.timelineItemValue}>{value}</div>
                    </div>
                ))}
            </Timeline.Item>
        )
    }))
